import classNames from 'classnames';
import React, { ChangeEvent, FocusEvent, memo, useMemo } from 'react';
import Div from './Div';
import Label from './Label';
import { FieldError, FieldErrors } from 'react-hook-form';
import VALIDATION_ERRORS from '@/constants/validationErrors';
import { inputOnlyNumbers } from '@/utils/formValidation';
import InfoButtonTooltip from './crisp/InfoIconToolTip';

const INPUT_SIZES = {
  xs: '',
  sm: '',
  base: 'text-body-lg py-3 px-4',
  lg: '',
  xl: '',
};

type InputProps = {
  className?: string;
  id?: string | number;
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
  label?: string;
  placeholder?: string;
  name: string;
  maxLength?: number;
  errors?: FieldErrors;
  value?: string | number;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;

  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'checkbox' | 'radio';
  dataTestId?: string;
  style?: React.CSSProperties;
  checked?: boolean;
  disabled?: boolean;
  wrapperClassName?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  info?: string;
  onWheel?: any;
};

// eslint-disable-next-line react/display-name
const Input = React.forwardRef(
  (
    {
      name,
      id,
      className,
      label,
      errors,
      disabled,
      placeholder,
      size = 'base',
      type = 'text',
      dataTestId,
      checked,
      style,
      autoFocus,
      autoComplete,
      onKeyDown,
      info,
      onWheel,
      ...restProps
    }: InputProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const fieldError: FieldError | undefined = (errors?.[name] as FieldError) ?? undefined;
    const errorMessage = fieldError?.message || VALIDATION_ERRORS[fieldError?.type];
    const isCheckbox = type === 'checkbox';
    const inputClasses = useMemo(
      () =>
        classNames(
          'border border-neutral-100 text-black/70 placeholder:text-black/30 foucs:outline focus:outline-blue-300 bg-white transition-all rounded-cxs',
          {
            [INPUT_SIZES[size]]: !isCheckbox,
            ['border-red-500']: errorMessage,
          },
          className,
          {
            '!text-black/30 cursor-not-allowed': disabled,
          },
        ),
      [size, className, errorMessage, isCheckbox, disabled],
    );

    return (
      <Div
        className={classNames('relative grid w-full', {
          'gap-s3': label || errorMessage,
        })}
      >
        <Label htmlFor={name}>{label}</Label>
        <input
          data-testid={dataTestId}
          ref={ref}
          type={type}
          name={name}
          id={name}
          autoComplete={autoComplete ?? 'off'}
          checked={checked}
          disabled={disabled}
          placeholder={placeholder}
          onWheel={onWheel}
          {...restProps}
          className={inputClasses}
          style={style}
          autoFocus={autoFocus}
          onKeyDown={type === 'number' && !onKeyDown ? inputOnlyNumbers : onKeyDown}
        />
        {!errorMessage && info && (
          <Div className="absolute right-4  top-1/2 z-30  transform -translate-y-1/3">
            <InfoButtonTooltip tooltipText={info} />
          </Div>
        )}
        <Div
          forceRender={!isCheckbox}
          className={classNames('text-body-base text-neutral-700', {
            'text-red-500': errorMessage,
          })}
        >
          {errorMessage}
        </Div>
      </Div>
    );
  },
);

export default memo(Input);
